import { ECHANNEL_APP_ID, ENCRYPTION_CONFIG } from '@/constants';
import { PaymentSource } from '@/enums/PaymentSource';
import { store } from '@/store';
import { ApplicationsRequest } from '@/types/Applications/ApplicationsRequest';
import { Applicant } from '@/types/Question/Assessment';
import { encryptApplicantsIdNo } from '@/utility/crypto';
import { createAsyncThunk } from '@reduxjs/toolkit';
import axiosInterceptorInstance from './axiosInterceptorInstance';

const trimApplicantsNameFields = (
  applicants: ApplicationsRequest['applicants'],
): ApplicationsRequest['applicants'] =>
  applicants.map((applicant) => ({
    ...applicant,
    name: {
      ...applicant.name,
      fullName: applicant.name.fullName.trim(),
      firstName: applicant.name.firstName?.trim(),
      lastName: applicant.name.lastName?.trim(),
      middleName: applicant.name.middleName?.trim(),
      nickName: applicant.name.nickName?.trim(),
    },
  }));

const trimPayoutNameFields = (
  payout: ApplicationsRequest['payout'],
): ApplicationsRequest['payout'] => ({
  ...payout,
  accountHolderName: payout.accountHolderName.trim(),
});

export const applications = createAsyncThunk(
  'applications',
  async ({ language }: { language: string }) => {
    const rootState = store.getState();
    const planSummary = rootState.planSummary;

    try {
      // Encrypt idNo for all applicants before sending to API
      const encryptedApplicants = await encryptApplicantsIdNo(
        planSummary.applicants,
        ENCRYPTION_CONFIG,
      );

      const request: ApplicationsRequest = {
        appId: ECHANNEL_APP_ID,
        refID: planSummary.referenceId,
        productCode: planSummary.productCode,
        paymentMode: planSummary.paymentMode,
        distributionChannel: planSummary.distributionChannel,
        preferredLang: language,
        payMethod: PaymentSource.Credit_Card,
        promoCode: planSummary.promoCode,
        agent: planSummary.agent,
        employee: planSummary.employee,
        consentConfirmation: planSummary.consentConfirmation,
        collection: planSummary.collection,
        payout: trimPayoutNameFields(planSummary.payout),
        suppression: planSummary.suppression,
        purposeOfTrx: planSummary.purposeOfTrx,
        applicants: trimApplicantsNameFields(encryptedApplicants),
        ...(rootState.planSummary.family && {
          family: rootState.planSummary.family,
        }),
      };

      const relativeUrl = 'applications/';
      const apiUrl = `/quote/?relativeUrl=${relativeUrl}`;
      const response = await axiosInterceptorInstance.post(apiUrl, request);
      return response.data;
    } catch (error) {
      return null;
    }
  },
);

export const getApplications = createAsyncThunk(
  'getApplications',
  async ({ referenceId }: { referenceId: string }) => {
    try {
      const relativeUrl = 'applications/' + referenceId;
      const apiUrl = `/quote/?relativeUrl=${relativeUrl}`;
      const response = await axiosInterceptorInstance.get(apiUrl);
      return response.data;
    } catch (error) {
      return null;
    }
  },
);

export const getQuestion = createAsyncThunk(
  'question',
  async ({ language }: { language: string }) => {
    const rootState = store.getState();
    const planSummary = rootState.planSummary;
    try {
      const request = {
        appId: 'EDP',
        refId: planSummary.referenceId,
        preferredLang: language,
        includeLiamQuestion: false,
      };

      const relativeUrl = 'applications/uw/questions';
      const apiUrl = `/quote/?relativeUrl=${relativeUrl}`;
      const response = await axiosInterceptorInstance.post(apiUrl, request);
      return response.data;
    } catch (error) {
      return null;
    }
  },
);

export const sendAnswer = createAsyncThunk(
  'assessments',
  async (applicants: Applicant[]) => {
    const rootState = store.getState();
    const planSummary = rootState.planSummary;
    try {
      const request = {
        appId: 'EDP',
        refId: planSummary.referenceId,
        applicants,
      };

      const relativeUrl = 'applications/uw/assessments';
      const apiUrl = `/quote/?relativeUrl=${relativeUrl}`;
      const response = await axiosInterceptorInstance.post(apiUrl, request);
      return response.data;
    } catch (error) {
      return null;
    }
  },
);

export const getFacility = createAsyncThunk(
  'facilities',
  async ({ applicationId }: { applicationId: string }) => {
    try {
      const request = {
        appId: applicationId,
        mode: 'full',
        lastSyncDate: '2024-05-25',
        resource: 'facility',
      };

      const relativeUrl = 'facilities/';
      const apiUrl = `/quote/?relativeUrl=${relativeUrl}`;
      const response = await axiosInterceptorInstance.post(apiUrl, request);
      return response.data;
    } catch (error) {
      return null;
    }
  },
);

export const submitPersonalDetails = createAsyncThunk(
  'applications',
  async ({
    applicationId,
    paymentMethod,
    language,
  }: {
    applicationId: string;
    paymentMethod: string;
    language: string;
  }) => {
    const rootState = store.getState();

    const planSummary = rootState.planSummary;
    try {
      // Encrypt idNo for all applicants before sending to API
      const encryptedApplicants = await encryptApplicantsIdNo(
        rootState.planSummary.applicants,
        ENCRYPTION_CONFIG,
      );

      const request: ApplicationsRequest = {
        appId: applicationId,
        agent: planSummary.agent,
        payout: trimPayoutNameFields(planSummary.payout),
        refID: planSummary.referenceId,
        productCode: planSummary.productCode,
        paymentMode: planSummary.quickQuoteData.paymentMode,
        distributionChannel: planSummary.distributionChannel,
        payMethod: paymentMethod,
        preferredLang: language,
        purposeOfTrx: planSummary.purposeOfTrx,
        applicants: trimApplicantsNameFields(encryptedApplicants),
        ...(rootState.planSummary.family && {
          family: rootState.planSummary.family,
        }),
      };

      const relativeUrl = 'applications/';
      const apiUrl = `/quote/?relativeUrl=${relativeUrl}`;
      const response = await axiosInterceptorInstance.post(apiUrl, request);

      return response.data;
    } catch (error) {
      return null;
    }
  },
);

export const getPremiumQuotation = createAsyncThunk(
  'quote',
  async ({ referenceId }: { referenceId: string }) => {
    try {
      const relativeUrl = 'quotation/' + referenceId;
      const apiUrl = `/quote/?relativeUrl=${relativeUrl}`;
      const response = await axiosInterceptorInstance.get(apiUrl);
      return response.data;
    } catch (error) {
      return null;
    }
  },
);
